import React, { useState, useEffect } from "react";
import { Clock, AlertCircle, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import useEmployeeProfile from "../../hooks/useEmployeeProfile";
import timesheetService from "../../api/services/timesheetService";
import formatDate from "../../utils/formatDate";

const PAGE_SIZE = 10;

const Timesheet = () => {
  const { setHeader } = usePageHeader();
  const userProfile = useEmployeeProfile();
  const [timesheets, setTimesheets] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState("");
  const [loading, setLoading] = useState(true);
  const [fiscalYearsLoading, setFiscalYearsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedPostCode, setSelectedPostCode] = useState("");
  const [selectedSiteCode, setSelectedSiteCode] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setHeader({
      icon: Clock,
      title: "Timesheet Details",
      subtitle: "View your work hours and timesheet entries",
    });
    return () => setHeader({ icon: null, title: "", subtitle: "" });
  }, [setHeader]);

  useEffect(() => {
    fetchFiscalYears();
  }, []);

  useEffect(() => {
    if (selectedFiscalYear && userProfile?.employeeNo) {
      fetchTimesheets();
    }
  }, [selectedFiscalYear, userProfile?.employeeNo]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFiscalYear, selectedPeriod, selectedPostCode, selectedSiteCode]);

  const fetchFiscalYears = async () => {
    setFiscalYearsLoading(true);
    try {
      const response = await timesheetService.getFiscalYears();
      const years = response.data.value || [];
      setFiscalYears(years);
      const activeYear = years.find((year) => year.active === true);
      const defaultYear = activeYear
        ? activeYear.fiscalYear
        : years[0]?.fiscalYear || "";
      setSelectedFiscalYear(defaultYear);
    } catch (err) {
      console.error("Failed to fetch fiscal years:", err);
      setError("Failed to load fiscal years.");
    } finally {
      setFiscalYearsLoading(false);
    }
  };

  const fetchTimesheets = async () => {
    if (!userProfile?.employeeNo || !selectedFiscalYear) return;
    setLoading(true);
    setError(null);
    try {
      const response = await timesheetService.getTimesheetDetailsByFiscalYear(
        selectedFiscalYear,
        userProfile.employeeNo,
      );
      setTimesheets(response.data.value || []);
    } catch (err) {
      console.error("Failed to fetch timesheets:", err);
      setError(err.message || "Failed to load timesheets. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Unique filter values derived from loaded data
  const uniquePeriods = [
    ...new Set(timesheets.map((ts) => ts.payCyclePeriod).filter(Boolean)),
  ].sort((a, b) => Number(a) - Number(b));

  const uniquePostCodes = [
    ...new Set(timesheets.map((ts) => ts.postNumber).filter(Boolean)),
  ].sort();

  const uniqueSiteCodes = [
    ...new Set(timesheets.map((ts) => ts.site).filter(Boolean)),
  ].sort();

  // Apply client-side filters, sort by shiftDate descending
  const filteredTimesheets = timesheets
    .filter((ts) => {
      if (selectedPeriod && String(ts.payCyclePeriod) !== String(selectedPeriod))
        return false;
      if (selectedPostCode && ts.postNumber !== selectedPostCode) return false;
      if (selectedSiteCode && ts.site !== selectedSiteCode) return false;
      return true;
    })
    .sort((a, b) => new Date(b.shiftDate) - new Date(a.shiftDate));

  // Totals driven from filtered (not paginated) data
  const totalHours = filteredTimesheets.reduce(
    (sum, ts) => sum + (ts.noOfUnits ?? 0),
    0,
  );
  const totalAmount = filteredTimesheets.reduce(
    (sum, ts) => sum + (ts.amount ?? 0),
    0,
  );

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredTimesheets.length / PAGE_SIZE));
  const pagedTimesheets = filteredTimesheets.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  if (fiscalYearsLoading) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-xl shadow-lg p-6 lg:p-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            <p className="ml-3 text-gray-600">Loading fiscal years...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <div
        className="bg-white rounded-xl shadow-lg flex flex-col"
        style={{ maxHeight: "calc(100vh - 140px)" }}
      >
        {/* Static header: filters */}
        <div className="px-3 pt-6 pb-4 flex-shrink-0">
          {/* Error */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Inline filter dropdowns — right-aligned */}
          <div className="flex items-end justify-end gap-3 flex-wrap">
            {/* Fiscal Year */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Financial Year</label>
              <div className="relative">
                <select
                  value={selectedFiscalYear}
                  onChange={(e) => setSelectedFiscalYear(e.target.value)}
                  className="appearance-none pl-3 pr-7 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[110px]"
                >
                  {fiscalYears.map((year) => (
                    <option key={year.fiscalYear} value={year.fiscalYear}>
                      {year.fiscalYear}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Period */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Period</label>
              <div className="relative">
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="appearance-none pl-3 pr-7 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[100px]"
                >
                  <option value="">All Periods</option>
                  {uniquePeriods.map((p) => (
                    <option key={p} value={String(p)}>
                      Period {p}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Budgeted Post Code */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">
                Budgeted Post Code
              </label>
              <div className="relative">
                <select
                  value={selectedPostCode}
                  onChange={(e) => setSelectedPostCode(e.target.value)}
                  className="appearance-none pl-3 pr-7 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[130px]"
                >
                  <option value="">All Post Codes</option>
                  {uniquePostCodes.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Site Code */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Site Code</label>
              <div className="relative">
                <select
                  value={selectedSiteCode}
                  onChange={(e) => setSelectedSiteCode(e.target.value)}
                  className="appearance-none pl-3 pr-7 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[100px]"
                >
                  <option value="">All Sites</option>
                  {uniqueSiteCodes.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable table with sticky header */}
        <div className="flex-1 overflow-y-auto rounded-b-xl overflow-x-auto">
          <table className="w-full table-auto ">
            <thead>
              <tr className="">
                <th className="sticky top-0 z-10 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap shadow-[0_-1px_0_0_#e5e7eb,0_1px_0_0_#e5e7eb]">
                  Financial Year
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap shadow-[0_-1px_0_0_#e5e7eb,0_1px_0_0_#e5e7eb]">
                  Pay Period / Month
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap shadow-[0_-1px_0_0_#e5e7eb,0_1px_0_0_#e5e7eb]">
                  Shift Date
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap shadow-[0_-1px_0_0_#e5e7eb,0_1px_0_0_#e5e7eb]">
                  Pay Period Start Date
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap shadow-[0_-1px_0_0_#e5e7eb,0_1px_0_0_#e5e7eb]">
                  Pay Period End Date
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap shadow-[0_-1px_0_0_#e5e7eb,0_1px_0_0_#e5e7eb]">
                  Budgeted Post Code
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap shadow-[0_-1px_0_0_#e5e7eb,0_1px_0_0_#e5e7eb]">
                  Site
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap shadow-[0_-1px_0_0_#e5e7eb,0_1px_0_0_#e5e7eb]">
                  Pay Element
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap shadow-[0_-1px_0_0_#e5e7eb,0_1px_0_0_#e5e7eb]">
                  Hours/Units
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap shadow-[0_-1px_0_0_#e5e7eb,0_1px_0_0_#e5e7eb]">
                  Rate
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap shadow-[0_-1px_0_0_#e5e7eb,0_1px_0_0_#e5e7eb]">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-3 py-16 text-center">
                    <div className="flex justify-center items-center gap-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-indigo-500"></div>
                      <p className="text-sm text-gray-500">
                        Loading timesheets...
                      </p>
                    </div>
                  </td>
                </tr>
              ) : pagedTimesheets.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-3 py-20 text-center">
                    <p className="text-base font-semibold text-gray-700 mb-1">
                      No Timesheet Entries Found
                    </p>
                    <p className="text-sm text-gray-400">
                      Try adjusting your filters, or check back once your
                      timesheets have been processed.
                    </p>
                  </td>
                </tr>
              ) : (
                pagedTimesheets.map((ts, index) => (
                  <tr
                    key={`${ts.site}-${ts.payCyclePeriod}-${index}`}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-3 py-3.5 whitespace-nowrap text-sm text-gray-600">
                      {ts.fiscalYear || "-"}
                    </td>
                    <td className="px-3 py-3.5 whitespace-nowrap text-sm text-gray-600 text-right">
                      {ts.payCyclePeriod || "-"}
                    </td>
                    <td className="px-3 py-3.5 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(ts.shiftDate)}
                    </td>
                    <td className="px-3 py-3.5 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(ts.startDate)}
                    </td>
                    <td className="px-3 py-3.5 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(ts.endDate)}
                    </td>
                    <td className="px-3 py-3.5 whitespace-nowrap text-sm text-gray-600">
                      {ts.postNumber || "-"}
                    </td>
                    <td className="px-3 py-3.5 whitespace-nowrap text-sm text-gray-600">
                      {ts.siteDescription || ts.site || "-"}
                    </td>
                    <td className="px-3 py-3.5 whitespace-nowrap text-sm text-gray-600">
                      {ts.payElementDescription || ts.payElement || "-"}
                    </td>
                    <td className="px-3 py-3.5 whitespace-nowrap text-sm text-gray-900 text-right">
                      {ts.noOfUnits != null ? ts.noOfUnits : "-"}
                    </td>
                    <td className="px-3 py-3.5 whitespace-nowrap text-sm text-gray-900 text-right">
                      {ts.rate != null ? `£${ts.rate.toFixed(2)}` : "-"}
                    </td>
                    <td className="px-3 py-3.5 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                      {ts.amount != null ? `£${ts.amount.toFixed(2)}` : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="rounded-b-xl overflow-clip">
              <tr className="border-t border-gray-200 bg-gray-50">
                {/* Pagination in first cell */}
                <td colSpan="8" className="px-3 py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </td>
                {/* Total hours under Hours/Units column */}
                <td className="px-3 py-4 whitespace-nowrap text-right">
                  <p className="text-xs text-gray-500">Total hours</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {totalHours % 1 === 0
                      ? totalHours.toFixed(0)
                      : totalHours.toFixed(2)}
                  </p>
                </td>
                {/* Rate column — empty */}
                <td className="px-3 py-4" />
                {/* Total amount under Amount column */}
                <td className="px-3 py-4 whitespace-nowrap text-right">
                  <p className="text-xs text-gray-500">Total amount</p>
                  <p className="text-sm font-semibold text-gray-900">
                    £
                    {totalAmount.toLocaleString("en-GB", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </p>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Timesheet;
